import { useEffect, useMemo, useState } from 'react';
import { outbox, type OutboxItem } from './outbox';
import type { OrderStatus } from './types';

/** action → 提交后订单应该变成的状态。用于乐观 UI */
const OPTIMISTIC_STATUS: Record<string, OrderStatus> = {
  ACCEPT: 'ACCEPTED',
  PICKUP: 'PICKED_UP',
  DELIVER: 'DELIVERED',
  FAIL: 'FAILED',
  RETRY: 'ACCEPTED',
};

export interface OutboxView {
  items: OutboxItem[];
  pending: OutboxItem[];
  conflicts: OutboxItem[];
  dead: OutboxItem[];
  /** 订单 id → 本地预期状态。渲染时覆盖服务器返回的状态 */
  overlay: Map<string, OrderStatus>;
  /** 有待同步操作的订单 id,用于打「待同步」角标 */
  pendingOrderIds: Set<string>;
}

export function useOutbox(): OutboxView {
  const [items, setItems] = useState<OutboxItem[]>([]);

  useEffect(() => outbox.subscribe(setItems), []);
  useEffect(() => outbox.start(), []);

  return useMemo(() => {
    const pending = items.filter((i) => i.status === 'QUEUED' || i.status === 'SYNCING');
    const conflicts = items.filter((i) => i.status === 'CONFLICT');
    const dead = items.filter((i) => i.status === 'DEAD');

    // 乐观 UI:本地立即变更状态,司机不需要等网络。
    // 冲突和死信不参与覆盖 —— 那些操作实际没生效,显示服务器状态才是对的
    const overlay = new Map<string, OrderStatus>();
    const pendingOrderIds = new Set<string>();
    for (const item of pending) {
      const next = OPTIMISTIC_STATUS[item.action];
      for (const id of item.orderIds) {
        pendingOrderIds.add(id);
        if (next) overlay.set(id, next);
      }
    }

    return { items, pending, conflicts, dead, overlay, pendingOrderIds };
  }, [items]);
}
